import type { Vec2 } from "@townsy/contracts";
import { isReservedKey, isTypingTarget, shouldBlockMovement } from "./focusGuard";
import { EMPTY_KEYS, NO_DRIVE, applyKey, cameraRelativeMove, combineIntents, driveIntent, isInteractKey, isRunKeyCode, rawVector, type DriveIntent, type KeyState } from "./keyboard";

/**
 * DOM wiring for movement input: WASD/arrows + a virtual joystick vector, converted to a
 * camera-relative unit vector on demand (walking) or to throttle/steer (driving, ADR-0006). E is
 * the get in / get out and sit / stand key; it obeys the same focus rules (typing in chat never drives or walks). Keys reset on blur / hidden tab / reconnect so a stuck
 * key never walks forever (docs/03 section 3). Shift held, or the touch Run toggle, runs (ADR-0018:
 * `isRunning()`; the server decides the speed). Everything decision-related lives in the pure
 * modules (`keyboard.ts`, `focusGuard.ts`).
 */
export interface InputControllerDeps {
  getCameraYaw(): number;
  isWalkMode(): boolean;
  isModalOpen(): boolean;
  isChatFocused(): boolean;
  /** E pressed while movement input is allowed: get in / get out of a car, sit down / stand up. */
  onInteract?(): void;
  /** The touch Run toggle (ADR-0018): true while it is switched on. */
  isRunToggled?(): boolean;
}

export class InputController {
  private keys: KeyState = EMPTY_KEYS;
  /** Shift held (ADR-0018: run). */
  private runKey = false;
  private joystick: Vec2 = { x: 0, z: 0 };
  private attached = false;
  private windowFocused = true;

  constructor(private readonly deps: InputControllerDeps) {}

  attach(): void {
    if (this.attached || typeof window === "undefined") return;
    this.attached = true;
    window.addEventListener("keydown", this.onKeyDown, { capture: true });
    window.addEventListener("keyup", this.onKeyUp, { capture: true });
    window.addEventListener("blur", this.onBlur);
    window.addEventListener("focus", this.onFocus);
    document.addEventListener("visibilitychange", this.onVisibility);
  }

  detach(): void {
    if (!this.attached) return;
    this.attached = false;
    window.removeEventListener("keydown", this.onKeyDown, { capture: true });
    window.removeEventListener("keyup", this.onKeyUp, { capture: true });
    window.removeEventListener("blur", this.onBlur);
    window.removeEventListener("focus", this.onFocus);
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.reset();
  }

  /** Zero every held key and the joystick (blur, reconnect, room change). */
  reset(): void {
    this.keys = EMPTY_KEYS;
    this.runKey = false;
    this.joystick = { x: 0, z: 0 };
  }

  setJoystick(v: Vec2): void {
    this.joystick = v;
  }

  get isBlocked(): boolean {
    return shouldBlockMovement({
      activeElement: typeof document === "undefined" ? null : document.activeElement,
      modalOpen: this.deps.isModalOpen(),
      chatFocused: this.deps.isChatFocused(),
      documentVisible: typeof document === "undefined" ? true : document.visibilityState === "visible",
      windowFocused: this.windowFocused,
      walkMode: this.deps.isWalkMode(),
    });
  }

  /** World-space, unit-clamped movement intent for this tick (zero when movement is blocked). */
  getIntent(): Vec2 {
    if (this.isBlocked) return { x: 0, z: 0 };
    const screen = combineIntents(rawVector(this.keys), this.joystick);
    return cameraRelativeMove(screen, this.deps.getCameraYaw());
  }

  /** Run instead of walk this tick: Shift held or the touch Run toggle on, and movement input allowed (ADR-0018). */
  isRunning(): boolean {
    if (this.isBlocked) return false;
    return this.runKey || (this.deps.isRunToggled?.() ?? false);
  }

  /** Throttle and steering for this tick (zero when movement input is blocked, e.g. typing in chat). */
  getDriveIntent(): DriveIntent {
    if (this.isBlocked) return NO_DRIVE;
    return driveIntent(this.keys, this.joystick);
  }

  private onKeyDown = (ev: KeyboardEvent): void => {
    if (isReservedKey(ev) || ev.repeat) return;
    if (isTypingTarget(ev.target as Element | null) || isTypingTarget(document.activeElement)) return;
    if (isInteractKey(ev)) {
      if (!this.isBlocked) this.deps.onInteract?.();
      return;
    }
    if (isRunKeyCode(ev.code)) {
      this.runKey = true;
      return;
    }
    const next = applyKey(this.keys, ev.code, true);
    if (next === this.keys) return;
    this.keys = next;
    // Arrow keys would otherwise scroll the page while walking.
    if (this.deps.isWalkMode() && ev.code.startsWith("Arrow")) ev.preventDefault();
  };

  private onKeyUp = (ev: KeyboardEvent): void => {
    // Always clear, even if focus moved into an input while the key was held.
    this.keys = applyKey(this.keys, ev.code, false);
    if (isRunKeyCode(ev.code)) this.runKey = false;
  };

  private onBlur = (): void => {
    this.windowFocused = false;
    this.reset();
  };

  private onFocus = (): void => {
    this.windowFocused = true;
  };

  private onVisibility = (): void => {
    if (document.visibilityState !== "visible") this.reset();
  };
}
